import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const LS_KEY = "nx_license_key";

/**
 * Persistent token balance pill in the TopBar.
 * Reads the saved Gumroad license key from localStorage and polls
 * /api/wallet/balance/{key} every 60s. If no key is saved, shows a
 * "Get a Token" CTA linking to /redeem.
 */
export default function TokenBadge() {
  const [licenseKey, setLicenseKey] = useState("");
  const [balance, setBalance] = useState(null);

  const refresh = useCallback(async (key) => {
    if (!key) {
      setBalance(null);
      return;
    }
    try {
      const res = await api.walletBalance(key);
      setBalance(res?.found ? Number(res.balance) || 0 : 0);
    } catch {
      setBalance(null);
    }
  }, []);

  useEffect(() => {
    const k = (typeof window !== "undefined" && window.localStorage?.getItem(LS_KEY)) || "";
    setLicenseKey(k);
    refresh(k);

    // Re-check when other tabs (or RedeemPage) save a new key
    const onStorage = (e) => {
      if (e.key === LS_KEY) {
        const nk = e.newValue || "";
        setLicenseKey(nk);
        refresh(nk);
      }
    };
    window.addEventListener("storage", onStorage);

    // Poll every 60s so balance stays fresh after /api/execute spends a token
    const iv = setInterval(() => {
      const cur = window.localStorage?.getItem(LS_KEY) || "";
      if (cur) refresh(cur);
    }, 60000);

    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(iv);
    };
  }, [refresh]);

  // No key saved → "Get a Token" CTA
  if (!licenseKey) {
    return (
      <Link
        to="/redeem"
        data-testid="topbar-get-token"
        className="hidden md:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--void)] transition-all duration-300"
      >
        <span className="text-[12px]">◆</span> Get a Token · $5
      </Link>
    );
  }

  // Key saved → show balance pill (clickable → /redeem)
  return (
    <Link
      to="/redeem"
      data-testid="topbar-token-balance"
      className="hidden md:inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 border border-[var(--cyan)] hover:border-[var(--violet)] transition-all duration-300 group"
      title="Click to manage your tokens"
    >
      <span className="text-[var(--text-3)]">Tokens</span>
      <span
        className="text-glow-cyan group-hover:text-glow-violet tabular-nums text-[12px]"
        data-testid="topbar-balance-count"
      >
        {balance == null ? "—" : balance}
      </span>
    </Link>
  );
}
